import { useCallback, useEffect, useRef, useState } from "react";

import { z } from "zod";

import { TimezoneOption } from "../types/TimezoneOption";

export const STORAGE_KEY = "booking_session_state";

/** Glepnir / host list key — stored date/slot Wizard state lives under {@link STORAGE_KEY}. */
export const GLEPNIR_BOOKING_SESSION_DATE_KEY = "booking_session_date";

/**
 * Clears wizard session keys **before React mounts** on `/embed/book`. The parent cannot
 * clear cross-origin iframe `sessionStorage` before load; Glepnir's CLEAR `postMessage` can
 * also fire before our listener attaches, after `useBookingSessionStorage` already
 * rehydrated stale date/time into state — so synchronous removal here fixes that race.
 */
export function clearStaleEmbedWizardSessionKeys(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(GLEPNIR_BOOKING_SESSION_DATE_KEY);
  } catch {
    // sessionStorage may be unavailable in certain iframe contexts
  }
}

type BookingSessionState = {
  selectedDate?: string;
  selectedDateTime?: string;
  timezone?: TimezoneOption;
  preparationNotes?: string;
  notesStepDone?: boolean;
};

type UseBookingSessionStorageReturn = {
  persistedState: BookingSessionState;
  persistSelectedDate: (date: string | undefined) => void;
  persistSelectedDateTime: (dateTime: string | undefined) => void;
  persistTimezone: (timezone: TimezoneOption | undefined) => void;
  persistPreparationNotes: (notes: string) => void;
  persistNotesStepDone: (done: boolean) => void;
  clear: () => void;
};

const TimezoneOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
});

const BookingSessionStateSchema = z.object({
  selectedDate: z.string().optional(),
  selectedDateTime: z.string().optional(),
  timezone: TimezoneOptionSchema.optional(),
  preparationNotes: z.string().optional(),
  notesStepDone: z.boolean().optional(),
});

const readFromStorage = (): BookingSessionState => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = BookingSessionStateSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
};

const writeToStorage = (state: BookingSessionState): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage may be unavailable in certain iframe contexts — fail silently
  }
};

export const useBookingSessionStorage = (): UseBookingSessionStorageReturn => {
  const [persistedState, setPersistedState] = useState<BookingSessionState>(
    () => readFromStorage(),
  );
  // Prevents the sync effect from writing {} back to storage after clear() removes the key.
  const skipNextWriteRef = useRef(false);

  useEffect(() => {
    if (skipNextWriteRef.current) {
      skipNextWriteRef.current = false;
      return;
    }
    writeToStorage(persistedState);
  }, [persistedState]);

  const updateState = useCallback((patch: Partial<BookingSessionState>) => {
    setPersistedState((prev) => ({ ...prev, ...patch }));
  }, []);

  const persistSelectedDate = useCallback(
    (date: string | undefined) => {
      updateState({ selectedDate: date });
    },
    [updateState],
  );

  const persistSelectedDateTime = useCallback(
    (dateTime: string | undefined) => {
      updateState({ selectedDateTime: dateTime });
    },
    [updateState],
  );

  const persistTimezone = useCallback(
    (timezone: TimezoneOption | undefined) => {
      updateState({ timezone });
    },
    [updateState],
  );

  const persistPreparationNotes = useCallback(
    (notes: string) => {
      updateState({ preparationNotes: notes });
    },
    [updateState],
  );

  const persistNotesStepDone = useCallback(
    (done: boolean) => {
      updateState({ notesStepDone: done });
    },
    [updateState],
  );

  const clear = useCallback(() => {
    skipNextWriteRef.current = true;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // sessionStorage may be unavailable in certain iframe contexts — fail silently
    }
    setPersistedState({});
  }, []);

  return {
    persistedState,
    persistSelectedDate,
    persistSelectedDateTime,
    persistTimezone,
    persistPreparationNotes,
    persistNotesStepDone,
    clear,
  };
};
